// The page's side of the desktop shell (apps/desktop) beside presence (desktopPresence.ts): whether the world draws
// frames, told to the shell so it repaints the window for the Steam overlay only while the page draws nothing; and the
// window's own title bar (the shell's custom frame on Windows and macOS): html classes the console's CSS lays the top
// bar out by (gc-frame while the page is the title bar, off in fullscreen; gc-controls-left when the system's buttons
// sit at its left end, as macOS's traffic lights do), the buttons' strip width as a CSS variable, and the window moved
// from a press and drag on any of the top bar's controls (barDrag.ts).
// Outside the shell this does nothing.

import type { World } from '../engine/world';
import { desktopBridge } from '../adapter/net/steam';
import { desktopInfo } from '../engine/desktop';
import { startBarDrag } from './barDrag';

/** a frame drawn this recently counts as drawing (ms) */
const DRAWN_MS = 300;
/** the minimize, maximize and close buttons' strip on Windows, when the page cannot read its geometry (CSS px) */
const CONTROLS_W = 138;
/** macOS's traffic lights' strip, when the shell does not give it (CSS px) */
const CONTROLS_LEFT_W = 78;

let frameStarted = false;
/** Lays the page out as the window's title bar while the shell draws its own frame (not in fullscreen). */
export function startDesktopFrame(): void {
  if (frameStarted) return;
  frameStarted = true;
  const info = desktopInfo();
  const bridge = desktopBridge();
  // the desktop app's window carries the game's name: Discord and the taskbar name the program after it
  if (bridge) document.title = 'GridController';
  if (!info || info.frame !== 'custom') return;
  // a press on any control of the top bar that moves drags the window (a shell without the call: the free space only)
  if (typeof bridge?.windowDrag === 'function') startBarDrag((phase, x, y) => bridge.windowDrag!(phase, x, y));
  const root = document.documentElement;
  root.style.setProperty('--gc-titlebar-h', `${info.titleBarHeight || 31}px`);
  const left = info.controlsSide === 'left';
  root.classList.toggle('gc-controls-left', left);
  const wco = (navigator as { windowControlsOverlay?: { visible: boolean; getTitlebarAreaRect(): DOMRect; addEventListener(t: string, f: () => void): void } }).windowControlsOverlay;
  const place = () => {
    // macOS: the traffic lights' strip has the width the shell gives (no window controls overlay to measure there);
    // Windows: the buttons sit right of the title bar area the overlay leaves to the page
    const r = !left && wco?.visible ? wco.getTitlebarAreaRect() : null;
    const w = left ? info.controlsWidth || CONTROLS_LEFT_W : r && r.width > 0 ? Math.max(0, window.innerWidth - (r.x + r.width)) : CONTROLS_W;
    root.style.setProperty('--gc-controls-w', `${Math.round(w)}px`);
  };
  let full = false;
  const apply = () => {
    root.classList.toggle('gc-frame', !full);
    place();
  };
  if (!left) {
    wco?.addEventListener('geometrychange', place);
    window.addEventListener('resize', place);
  }
  if (bridge?.onFrame)
    bridge.onFrame((st) => {
      full = st.fullscreen;
      apply();
    });
  else apply();
}

/** Tells the shell whether the world draws frames (checked four times a second, sent on a change). */
export function startDesktopDrawing(world: World): void {
  const bridge = desktopBridge();
  if (!bridge || typeof bridge.drawing !== 'function') return;
  let last: boolean | null = null;
  window.setInterval(() => {
    const on = document.visibilityState === 'visible' && performance.now() - world.stats.lastAt < DRAWN_MS;
    if (on === last) return;
    last = on;
    bridge.drawing!(on);
  }, 250);
}
